import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { AlertCircle, CheckCircle2, ChevronDown, Trash2 } from "./icons";
import { translateUi } from "./i18n/index";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type InstructorSelectOption = {
  id: string;
  full_name?: string;
  role?: string;
};

/** Split a stored role string into individual labels for pills. */
export function splitInstructorRoleLabels(role?: string | null): string[] {
  return String(role ?? "")
    .split(/[,;/|]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Soft pastel pill tones — muted, not neon. Stable per role label. */
const ROLE_PILL_TONES_LIGHT = [
  "border-sky-200/80 bg-sky-50 text-sky-800",
  "border-teal-200/80 bg-teal-50 text-teal-800",
  "border-violet-200/80 bg-violet-50 text-violet-800",
  "border-amber-200/80 bg-amber-50 text-amber-900",
  "border-rose-200/70 bg-rose-50 text-rose-800",
  "border-emerald-200/80 bg-emerald-50 text-emerald-800",
  "border-indigo-200/80 bg-indigo-50 text-indigo-800",
  "border-cyan-200/80 bg-cyan-50 text-cyan-800",
] as const;

const ROLE_PILL_TONES_DARK = [
  "border-sky-700/50 bg-sky-950/55 text-sky-200",
  "border-teal-700/50 bg-teal-950/55 text-teal-200",
  "border-violet-700/50 bg-violet-950/55 text-violet-200",
  "border-amber-700/45 bg-amber-950/50 text-amber-200",
  "border-rose-700/45 bg-rose-950/50 text-rose-200",
  "border-emerald-700/50 bg-emerald-950/55 text-emerald-200",
  "border-indigo-700/50 bg-indigo-950/55 text-indigo-200",
  "border-cyan-700/50 bg-cyan-950/55 text-cyan-200",
] as const;

/** Prefer familiar colours for common instructor-related roles. */
const ROLE_PILL_ALIASES: Record<string, number> = {
  instructor: 6,
  "senior driver instructor": 0,
  "driving instructor": 0,
  "driver instructor": 0,
  assessor: 3,
  verifier: 5,
  tolo: 1,
  dcp: 2,
  "training delivery": 7,
  "assurance manager": 4,
};

function rolePillToneIndex(role: string): number {
  const key = role.trim().toLowerCase();
  if (key in ROLE_PILL_ALIASES) return ROLE_PILL_ALIASES[key]!;
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return hash % ROLE_PILL_TONES_LIGHT.length;
}

function rolePillClass(role: string, dark: boolean, selected?: boolean): string {
  if (selected) {
    return dark
      ? "border-white/25 bg-white/15 text-indigo-50"
      : "border-white/35 bg-white/20 text-white";
  }
  const idx = rolePillToneIndex(role);
  return dark ? ROLE_PILL_TONES_DARK[idx]! : ROLE_PILL_TONES_LIGHT[idx]!;
}

function RolePills({
  roles,
  dark,
  selected,
  compact,
}: {
  roles: string[];
  dark: boolean;
  selected?: boolean;
  compact?: boolean;
}) {
  if (roles.length === 0) return null;
  return (
    <span className={cn("flex flex-wrap gap-1", compact ? "mt-0.5" : "mt-1.5")}>
      {roles.map((role) => (
        <span
          key={role}
          className={cn(
            "inline-flex max-w-full items-center truncate rounded-full border px-2 py-0.5 text-[10px] font-semibold leading-tight tracking-wide",
            rolePillClass(role, dark, selected),
          )}
          title={role}
        >
          {translateUi(role)}
        </span>
      ))}
    </span>
  );
}

type InstructorSelectDropdownProps = {
  value: string;
  options: InstructorSelectOption[];
  onChange: (id: string) => void;
  dark?: boolean;
  className?: string;
  disabled?: boolean;
};

/**
 * Custom instructor picker so role labels can render as pills (native &lt;select&gt; cannot).
 */
export function InstructorSelectDropdown({
  value,
  options,
  onChange,
  dark = false,
  className,
  disabled = false,
}: InstructorSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value],
  );
  const selectedRoles = useMemo(
    () => splitInstructorRoleLabels(selected?.role),
    [selected?.role],
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerLabel = selected
    ? selected.full_name || selected.id
    : translateUi("Not allocated");

  return (
    <div className={cn(className)}>
      <div ref={rootRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "w-full rounded-xl border px-3 py-2 text-left text-sm transition-colors",
            "flex items-start justify-between gap-2",
            dark
              ? "border-slate-600 bg-slate-900 text-slate-100 hover:border-slate-500"
              : "border-slate-300 bg-white text-slate-900 hover:border-slate-400",
            open && (dark ? "ring-2 ring-indigo-500/40 border-indigo-400" : "ring-2 ring-indigo-500/30 border-indigo-400"),
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <span className="min-w-0 flex-1">
            <span className="block font-medium truncate">{triggerLabel}</span>
            {selectedRoles.length > 0 ? (
              <RolePills roles={selectedRoles} dark={dark} compact />
            ) : null}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 shrink-0 mt-0.5 transition-transform",
              dark ? "text-slate-400" : "text-slate-500",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>

        {open ? (
          <ul
            id={listId}
            role="listbox"
            className={cn(
              "absolute z-40 mt-1.5 max-h-72 w-full overflow-y-auto rounded-xl border py-1 shadow-lg",
              dark ? "border-slate-600 bg-slate-900" : "border-slate-200 bg-white",
            )}
          >
            <li role="option" aria-selected={!value}>
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 transition-colors",
                  !value
                    ? dark
                      ? "bg-indigo-600 text-white"
                      : "bg-indigo-600 text-white"
                    : dark
                      ? "text-slate-200 hover:bg-slate-800"
                      : "text-slate-800 hover:bg-slate-50",
                )}
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                {!value ? <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden /> : <span className="w-4" />}
                <span className="font-medium">{translateUi("Not allocated")}</span>
              </button>
            </li>
            {options.map((ins) => {
              const roles = splitInstructorRoleLabels(ins.role);
              const isSelected = value === ins.id;
              return (
                <li key={ins.id} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={cn(
                      "w-full px-3 py-2.5 text-left text-sm transition-colors",
                      isSelected
                        ? "bg-indigo-600 text-white"
                        : dark
                          ? "text-slate-100 hover:bg-slate-800"
                          : "text-slate-900 hover:bg-slate-50",
                    )}
                    onClick={() => {
                      onChange(ins.id);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-start gap-2">
                      {isSelected ? (
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
                      ) : (
                        <span className="w-4 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold truncate">{ins.full_name || ins.id}</span>
                        <RolePills roles={roles} dark={dark} selected={isSelected} />
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <p className={cn("mt-1.5 text-[11px] leading-relaxed", dark ? "text-slate-400" : "text-slate-500")}>
        {translateUi(
          "Only people with Instructor or Senior Driver Instructor roles are shown here.",
        )}
      </p>
    </div>
  );
}

/** History is newest-first. Each entry starts a period that ends at the next newer event (or Current). */
export function getInstructorAssignmentPeriod(
  historyNewestFirst: Array<{ assigned_at?: string | null }>,
  index: number,
): { fromAt: string; toAt: string | null } {
  const fromAt = String(historyNewestFirst[index]?.assigned_at ?? "").trim();
  if (index <= 0) return { fromAt, toAt: null };
  const toAt = String(historyNewestFirst[index - 1]?.assigned_at ?? "").trim() || null;
  return { fromAt, toAt };
}

export function formatInstructorAuditDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

/** From / To block for the assignment audit trail (To = Current when still active). */
export function InstructorAssignmentPeriodLabel({
  fromAt,
  toAt,
  dark = false,
}: {
  fromAt: string;
  toAt: string | null;
  dark?: boolean;
}) {
  const isCurrent = !toAt;
  return (
    <div
      className={cn(
        "text-right text-[11px] leading-snug tabular-nums shrink-0",
        dark ? "text-slate-400" : "text-slate-500",
      )}
    >
      <div>
        <span className={cn("font-medium", dark ? "text-slate-500" : "text-slate-400")}>
          {translateUi("From")}{" "}
        </span>
        <span className="font-semibold">{formatInstructorAuditDateTime(fromAt)}</span>
      </div>
      <div className="mt-0.5">
        <span className={cn("font-medium", dark ? "text-slate-500" : "text-slate-400")}>
          {translateUi("To")}{" "}
        </span>
        {isCurrent ? (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
              dark ? "bg-emerald-950/50 text-emerald-300" : "bg-emerald-50 text-emerald-700",
            )}
          >
            {translateUi("Current")}
          </span>
        ) : (
          <span className="font-semibold">{formatInstructorAuditDateTime(toAt)}</span>
        )}
      </div>
    </div>
  );
}

export type InstructorAssignmentAuditEntry = {
  id?: string;
  action?: string | null;
  instructor_name?: string | null;
  instructor_employee_id?: string | null;
  previous_instructor_name?: string | null;
  previous_instructor_employee_id?: string | null;
  assigned_at?: string | null;
  assigned_by?: string | null;
  assigned_by_name?: string | null;
};

/** Single assignment audit trail card (gap warning + admin delete). */
export function InstructorAssignmentAuditCard({
  entry,
  fromAt,
  toAt,
  dark = false,
  canDelete = false,
  deleting = false,
  onDelete,
}: {
  entry: InstructorAssignmentAuditEntry;
  fromAt: string;
  toAt: string | null;
  dark?: boolean;
  canDelete?: boolean;
  deleting?: boolean;
  onDelete?: () => void;
}) {
  const actionRaw = String(entry.action ?? "").trim().toLowerCase();
  const isCleared = actionRaw === "cleared" || !String(entry.instructor_name ?? "").trim();
  const actionLabel =
    actionRaw === "reassigned"
      ? translateUi("Reassigned")
      : isCleared
        ? translateUi("Cleared")
        : translateUi("Assigned");
  const whoDidRaw = String(entry.assigned_by_name || entry.assigned_by || "").trim();
  const whoDid = whoDidRaw && !whoDidRaw.includes("@") ? whoDidRaw : translateUi("Unknown");
  const gapWarning = translateUi(
    "No instructor allocated — check what the trainee was doing during this period",
  );

  return (
    <div
      className={cn(
        "rounded-xl border p-3 sm:p-3.5",
        isCleared
          ? dark
            ? "border-rose-800/40 bg-gradient-to-r from-rose-950/35 via-slate-900/40 to-slate-900/40"
            : "border-rose-200/90 bg-gradient-to-r from-rose-50 via-white to-white"
          : dark
            ? "border-slate-700 bg-slate-900/40"
            : "border-slate-200 bg-white",
      )}
    >
      <div
        className={cn(
          "grid gap-3",
          isCleared
            ? "grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] sm:items-center"
            : "grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start",
        )}
      >
        <div className="min-w-0">
          <span
            className={cn(
              "inline-flex text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full",
              isCleared
                ? dark
                  ? "bg-slate-700/90 text-slate-300 ring-1 ring-slate-600/60"
                  : "bg-slate-100 text-slate-600 ring-1 ring-slate-200/80"
                : dark
                  ? "bg-sky-900/50 text-sky-300"
                  : "bg-sky-100 text-sky-800",
            )}
          >
            {actionLabel}
          </span>
          <p className={cn("mt-2 text-sm font-semibold tracking-tight", dark ? "text-slate-100" : "text-slate-900")}>
            {entry.instructor_name || translateUi("Not allocated")}
          </p>
          {(entry.previous_instructor_name || entry.previous_instructor_employee_id) && (
            <p className={cn("mt-1 text-xs", dark ? "text-slate-400" : "text-slate-500")}>
              {translateUi("Previously")}: {entry.previous_instructor_name || translateUi("Not allocated")}
            </p>
          )}
          <p className={cn("mt-2 text-xs", dark ? "text-slate-400" : "text-slate-500")}>
            {translateUi("By")} <span className="font-semibold">{whoDid}</span>
          </p>
        </div>

        {isCleared ? (
          <div className="flex items-center justify-center px-1 sm:px-2">
            <span
              role="status"
              className={cn(
                "inline-flex max-w-full items-center justify-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold leading-snug tracking-tight shadow-sm",
                dark
                  ? "bg-rose-950/75 text-rose-100 ring-1 ring-inset ring-rose-700/55"
                  : "bg-rose-600 text-white ring-1 ring-inset ring-rose-700/25",
              )}
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0 opacity-95" aria-hidden />
              <span className="text-center">{gapWarning}</span>
            </span>
          </div>
        ) : null}

        <div className="flex items-start justify-end gap-2 shrink-0">
          <InstructorAssignmentPeriodLabel fromAt={fromAt} toAt={toAt} dark={dark} />
          {canDelete && onDelete ? (
            <button
              type="button"
              title={translateUi("Delete audit entry")}
              aria-label={translateUi("Delete audit entry")}
              disabled={deleting}
              onClick={onDelete}
              className={cn(
                "mt-0.5 p-1.5 rounded-lg transition-colors disabled:opacity-50",
                dark
                  ? "text-rose-300 hover:bg-rose-950/50 hover:text-rose-200"
                  : "text-rose-600 hover:bg-rose-50 hover:text-rose-700",
              )}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
